import { useEffect, useState } from 'react';

// What an overlay of the page carries: role="dialog" with aria-modal="true";
// a floating window that leaves the page usable (the mail composer) is not
// modal, and says it covers the corner with data-hides-intercom-launcher.
const HIDING_ATTRIBUTE = 'data-hides-intercom-launcher';
const OVERLAY_SELECTOR = `[aria-modal="true"], [${HIDING_ATTRIBUTE}]`;
// The messenger's own frames: opening it must not hide its launcher.
const INTERCOM_SELECTOR = '[id^="intercom"], [class*="intercom"]';

export const hasOpenOverlay = () =>
  Array.from(document.querySelectorAll(OVERLAY_SELECTOR)).some(
    (element) => !element.closest(INTERCOM_SELECTOR),
  );

/**
 * Hides the Intercom launcher while anything is open over the page: it
 * floats over the bottom right corner, where slide-overs and modals put their
 * actions. The shared slide-over and modal say so through
 * `isSharedOverlayOpen`; any other overlay by carrying aria-modal="true",
 * watched on the DOM, so that a new panel needs nothing more to hide it.
 *
 * Through the global rather than the SDK's `update`: that helper memoises
 * window.Intercom on its first call and returns a no-op when the messenger
 * has not booted, its boot being asynchronous. The snippet's own queue
 * handles a call that arrives early; `bootKey` (the user the messenger boots
 * with) applies the state again once it has.
 */
const useHideIntercomLauncher = (isSharedOverlayOpen, bootKey) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(hasOpenOverlay);

  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsOverlayOpen(hasOpenOverlay()),
    );
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal', HIDING_ATTRIBUTE],
    });
    return () => observer.disconnect();
  }, []);

  const hideLauncher = Boolean(isSharedOverlayOpen) || isOverlayOpen;

  useEffect(() => {
    if (typeof window.Intercom !== 'function') return;
    window.Intercom('update', { hide_default_launcher: hideLauncher });
  }, [hideLauncher, bootKey]);
};

export default useHideIntercomLauncher;
